import React, { useState, useEffect, useMemo, useCallback } from 'react';
// import {
//   Button,
// } from '@material-ui/core';
import { useParams, useLocation } from 'react-router-dom';
import { ReactFormGenerator, ElementStore } from 'components_newui/formBuilder/react-form-builder-innovate/src/index';
import { parseArraySafely } from '../utils';
import CustomButton from 'components_newui/customButton/CustomButton';


/*****************************
 * Scorecard - Single File JS
 *****************************/

/** Safe numeric coercion */
const coerceNum = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return NaN;
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : NaN;
  }
  if (typeof v === 'boolean') return v ? 1 : 0;
  return NaN;
};

/**
 * Build lookup: element.id -> option.key -> option.value (fallback to text)
 * Supports RadioButtons/Checkboxes/Dropdown (same options shape).
 */
const buildOptionKeyValueMap = (formDef) =>
  (Array.isArray(formDef) ? formDef : []).reduce((acc, el) => {
    const isChoice =
      el &&
      (el.element === 'RadioButtons' ||
        el.element === 'Checkboxes' ||
        el.element === 'Dropdown');

    if (!isChoice || !Array.isArray(el.options) || !el.id) return acc;

    const keyMap = el.options.reduce((m, opt) => {
      if (!opt || !opt.key) return m;
      // prefer value; fallback to text
      const logical = (opt.value ?? opt.text ?? '').toString();
      m[opt.key] = logical;
      return m;
    }, {});

    acc[el.id] = keyMap;
    return acc;
  }, {});

/**
 * Resolve raw answer:
 * - Picks value/selectedValue/answer
 * - If array (e.g., ["radiobuttons_option_*"]), use first
 * - If key is found in optionKeyValueMap[id], translate to logical (e.g., "yes"/"may_be"/"no")
 */
const resolveRawAnswer = (resp, optionKeyValueMap) => {
  const id = resp && resp.id;

  const candidate =
    (resp && (resp.value ?? resp.selectedValue ?? resp.answer)) ?? resp;

  const first =
    Array.isArray(candidate) && candidate.length > 0 ? candidate[0] : candidate;

  const keyMap = optionKeyValueMap && id && optionKeyValueMap[id];
  if (keyMap &&
    typeof first === 'string' &&
    Object.prototype.hasOwnProperty.call(keyMap, first)) {
    return keyMap[first];
  }

  return first;
};

/**
 * Map radio-like strings to 0..10 scale:
 * - yes    -> 10
 * - may_be -> 5 (also accepts "maybe"/"may be")
 * - no     -> 0
 * Otherwise tries numeric coercion (for normal ratings like 8/10).
 */
const mapRadioToScore0to10 = (raw) => {
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    if (v === 'yes' || v === 'yes_') return 10;
    if (v === 'no_not_on_a_roadmap' || v === 'no_not_on_a_roadmap_') return 10;
    if (v === 'may_be' || v === 'maybe' || v === 'may be') return 5;
    if (v === 'partially' || v === 'partially_') return 5;
    if (v === 'no' || v === 'no_') return 0;
    if (v === 'yes_on_a_roadmap' || v === 'yes_on_a_roadmap_') return 0;
    return coerceNum(raw);
  }
  if (typeof raw === 'boolean') return raw ? 10 : 0;
  return coerceNum(raw);
};

/**
 * Calculate weighted ratings (no distribution):
 * - weights: [{ id, weight }]
 * - responses: [{ id, value }] (value can be option key array)
 * - totalStars: max rating scale (default 10)
 * - optionKeyValueMap: produced by buildOptionKeyValueMap(formDefinition)
 */
const calculateWeightedRatings = (weights, responses, totalStars = 10, optionKeyValueMap = {}) => {
  const diagnostics = [];
  const errors = [];

  const respArr = Array.isArray(responses) ? responses : [];
  const weightsArr = Array.isArray(weights) ? weights : [];

  const totalStarsNum = coerceNum(totalStars);
  if (!Number.isFinite(totalStarsNum) || totalStarsNum <= 0) {
    errors.push(`Invalid totalStars: "${totalStars}" → must be a positive number.`);
    return {
      totalQuestions: respArr.length,
      weightedQuestions: weightsArr.length,
      totalWeightage: 0,
      individualRatings: [],
      totalRating: 0,
      percentage: 0,
      diagnostics,
      errors
    };
  }

  // Build weight map (first occurrence wins)
  const buildWeightMapResult = weightsArr.reduce(
    (acc, w) => {
      const id = w && w.id;
      const weightNum = coerceNum(w && w.weight);

      if (!id) {
        acc.diagnostics.push('A weight item without id was ignored.');
        return acc;
      }

      if (!Object.prototype.hasOwnProperty.call(acc.map, id)) {
        acc.map[id] = weightNum;
        if (!Number.isFinite(weightNum)) {
          acc.diagnostics.push(`Weight for id "${id}" is non-numeric ("${w && w.weight}").`);
        }
      } else {
        acc.diagnostics.push(`Duplicate weight entry for id "${id}". First occurrence used.`);
      }

      return acc;
    },
    { map: {}, diagnostics: [] }
  );

  diagnostics.push(...buildWeightMapResult.diagnostics);
  const weightMap = buildWeightMapResult.map;

  const totalQuestions = respArr.length;
  const weightedQuestions = Object.values(weightMap).filter(Number.isFinite).length;
  const totalWeightageNumeric = Object.values(weightMap).reduce(
    (sum, w) => sum + (Number.isFinite(w) ? w : 0),
    0
  );

  // Compute ratings
  const computeResult = respArr.reduce(
    (acc, resp) => {
      const id = resp && resp.id;

      const rawAnswer = resolveRawAnswer(resp, optionKeyValueMap);
      const received = mapRadioToScore0to10(rawAnswer);

      const mappedWeight = Object.prototype.hasOwnProperty.call(weightMap, id)
        ? weightMap[id]
        : 0; // no distribution

      const weight = Number.isFinite(mappedWeight) ? mappedWeight : 0;
      if (!Number.isFinite(mappedWeight)) {
        acc.diagnostics.push(`Weight for id "${id}" is NaN; using 0.`);
      }

      let rating = NaN;
      if (!Number.isFinite(received)) {
        acc.diagnostics.push(
          `Received value for id "${id}" is non-numeric ("${Array.isArray(rawAnswer) ? JSON.stringify(rawAnswer) : String(rawAnswer)}").`
        );
      } else {
        rating = (received / totalStarsNum) * (weight / 100);
        if (Number.isFinite(rating)) {
          acc.totalRatingFloat += rating;
        } else {
          acc.diagnostics.push(`Computed rating for id "${id}" is NaN.`);
        }
      }

      acc.rows.push({
        id,
        received: Number.isFinite(received) ? received : null,
        weight,
        rating: Number.isFinite(rating) ? rating.toFixed(4) : 'NaN',
        rawAnswer
      });

      return acc;
    },
    { rows: [], totalRatingFloat: 0, diagnostics: [] }
  );

  diagnostics.push(...computeResult.diagnostics);
  const percentage = Math.round(computeResult.totalRatingFloat * 100);

  return {
    totalQuestions,
    weightedQuestions,
    totalWeightage: Number(totalWeightageNumeric.toFixed(4)),
    individualRatings: computeResult.rows,
    totalRating: Math.round(computeResult.totalRatingFloat),
    percentage,
    diagnostics,
    errors
  };
};


const EvaluationScorecardForm = (props) => {
  // console.log("props=====================++++++++++++++++ EvaluationScorecardForm.jsx", props)
  const { 
    // jsonData=[],
    // ideaIdKey, 
    // challengeKey, 
    loginUserEmail, 
    postInnovateIdeaEvaluatorRatingSaveFn, 
     innovateShowPopupEvaluatorAssignedData, 
     getJsonEvaluatorForRating, 
   innovateShowPopupEvaluatorAssignedDataIsLoading, getJsonEvaluatorForRatingIsLoading, 
    // isHackathonChallenge,
    putInnovateIdeaEvaluatorRatingEditFn:handleEvaluatorRatingEdit ,
    getIdeaDetailsResponse,


    getJsonChallengeTemplateId,
    getScorecardJsonBasedOnChallengeTemplateIdFn,
    getShowPopupForEvaluatorScorecardAssignedIdeaFn,
    getUserValidateEvaluatorScorecardRoleFn,
    getListEvaluatorAssignedForIdeaFn,
    getIdeaDetailFn,
    match,
    getListEvaluatorAssignedIdea,
    getJsonRatedEvaluatorScorecardFn,
  } = props || {};

  //  console.log("props======+++++++++ match", match, match.params)
  const getIdeaIdFromURL = match.params.id;
  const [formData, setFormData] = useState([]);
  const [error, setError] = useState('');
  const [submitActionType, setSubmitActionType] = useState('SUBMIT'); // Tracks 'SUBMIT' vs 'SAVE'
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  console.log("props======+++++++++ queryParams", queryParams, location)
  const flagFromUrl = queryParams.get('flag'); // Extracts ?flag=E or B
  console.log("props======+++++++++ flagFromUrl", flagFromUrl, )
  const jsonData = getJsonChallengeTemplateId?.scoreCardJson?.value || []

  // console.log("props============jsonData=========++++++++++++++++ EvaluationScorecardForm.jsx", jsonData, getIdeaIdFromURL)
  // console.log("props======+++++++++ innovateShowPopupEvaluatorAssignedData.jsx", getJsonEvaluatorForRating)


  const scorecardPopupIdeaEvaluationId = getJsonEvaluatorForRating?.id;
  //  console.log("props======+++++++++ scorecardPopupIdeaEvaluationId", scorecardPopupIdeaEvaluationId,getJsonEvaluatorForRating)
  const ideaEvaluationScoreCardAnswerJson = getJsonEvaluatorForRating?.scoreCardAnswerJson?.value;
  const scorecardPopupFlagRecevive = getJsonEvaluatorForRating?.businessScoreCardFlag;
  const ratingStatusFlagReceive = getJsonEvaluatorForRating?.ratingStatus;
  const ratingStatusIsPending = String(ratingStatusFlagReceive ?? '').trim().toUpperCase() === 'PENDING';


  const idea = getIdeaDetailsResponse?.ideaForInnovater || {};
  const ideaTemplateId = idea?.challenge?.ideaTemplateId
  const challengeKey = idea?.challenge?.id
  //  Remove items where userEmail does NOT match loginUserEmail  
  const filteredDataGetListEvaluatorAssignedIdea = getListEvaluatorAssignedIdea.filter(item => item.userEmail === loginUserEmail);
  const challengeTemplateId = idea?.challenge?.challengeTemplateId // this template used for double scorecard
  //!!!!!!!!!!!!!!!!!!!!!! important flag to maintain isHackthon scorecard and innovate south africa scorecard
  const check_IsHackathonChallenge = idea?.challenge?.isHackathonChallenge
  
  const isHackathonChallenge = check_IsHackathonChallenge ?? false;


  useEffect(() => {
    getIdeaDetailFn?.(getIdeaIdFromURL);
    getListEvaluatorAssignedForIdeaFn?.(getIdeaIdFromURL);
  }, [getIdeaIdFromURL]);

    // this called  api for fetch json scorecard  Question based challengetemplateID
    useEffect(() => {
      if (challengeTemplateId) {
        getScorecardJsonBasedOnChallengeTemplateIdFn(challengeTemplateId)
      }
    }, [challengeTemplateId])
  
  // *********imp********
  // load scorecard this api call for getch scorecardPopupIdeaEvaluationId and other info
    useEffect(() => {
    // 1. Extract parameters from the URL (adjust based on your routing library)
    // 2. Execute the API call safely on the new page context
    if (getIdeaIdFromURL && flagFromUrl) {
      getJsonRatedEvaluatorScorecardFn({ 
        ideaIdKey: getIdeaIdFromURL, 
        userEmail: loginUserEmail, // Ensure user session/email is available on this page
        businessSCFlag: flagFromUrl 
      });
    }
  }, [loginUserEmail,flagFromUrl,getIdeaIdFromURL]); 
  

  // 1) Derive the correct flag for the logged-in evaluator and current idea
  const businessSCFlag = useMemo(() => {
    // Guard array presence
    if (!Array.isArray(filteredDataGetListEvaluatorAssignedIdea)) return "";

    // Find matching record by both ideaIdKey and userEmail (safer than index 0)
    const matchUser = filteredDataGetListEvaluatorAssignedIdea.find(
      (x) =>
        x?.ideaIdKey === getIdeaIdFromURL &&
        x?.userEmail?.toLowerCase?.() === loginUserEmail?.toLowerCase?.()
    );
    // Return flag if present, else empty string
    return matchUser?.businessScoreCardFlag ?? "";
  }, [filteredDataGetListEvaluatorAssignedIdea, getIdeaIdFromURL, loginUserEmail]);

  // console.log("businessSCFlag==< validate", businessSCFlag)
  // 2) Make a stable payload object that includes the derived flag
  const objUserAndIdeaID = useMemo(() => {
    return {
      ideaIdKey: getIdeaIdFromURL,
      userEmail: loginUserEmail,
      businessSCFlag, // <-- guaranteed to be resolved by memo above
    };
  }, [getIdeaIdFromURL, loginUserEmail, businessSCFlag]);


   // 3) Call only when all inputs are ready (prevents firing with empty flag)
    useEffect(() => {
      if (getIdeaIdFromURL && loginUserEmail && businessSCFlag) {
        getUserValidateEvaluatorScorecardRoleFn(objUserAndIdeaID);
      }
    }, [getIdeaIdFromURL, loginUserEmail, businessSCFlag]);


  const prefillAnswers = useMemo(() => {
    let raw = ideaEvaluationScoreCardAnswerJson;
    if (raw && typeof raw === 'object' && 'value' in raw) {
      raw = raw.value;
    }
    return parseArraySafely(raw, []);
  }, [ideaEvaluationScoreCardAnswerJson]);

  let convertJsonParseData = null;
  try {
    if (jsonData && typeof jsonData === "string") {
      convertJsonParseData = JSON.parse(jsonData);
    } else {
      console.warn("jsonData is empty or not a string:", jsonData);
    }
  } catch (err) {
    console.error("Failed to parse JSON:", err);
    convertJsonParseData = {};
  }

  const questions = useMemo(() => parseArraySafely(convertJsonParseData, []), [convertJsonParseData]);
  const prefill = useMemo(() => parseArraySafely(prefillAnswers, []), [prefillAnswers]);

   // Sync Prefill answers to local state once loaded
  useEffect(() => {
    if (prefill && prefill.length > 0) {
      setFormData(prefill);
    }
  }, [prefill]);

  const weightWithIdArr = useMemo(() => {
    return (Array.isArray(questions) ? questions : [])
      .filter(el => Number.isFinite(coerceNum(el && el.weight)))
      .map(el => ({ id: el.id, weight: coerceNum(el.weight) }));
  }, [questions]);

  // Unified Request Object Generator helper
  const createPayload = useCallback((currentFormData) => {
    //  console.warn("currentFormData========>", currentFormData);
    const optionKeyValueMapRadio = buildOptionKeyValueMap(questions);
    const calcWeightedRating = calculateWeightedRatings(weightWithIdArr, currentFormData, 10, optionKeyValueMapRadio);
    const roundCalcRating = calcWeightedRating?.percentage || 0;
    const defaultFlag = isHackathonChallenge ? 'E' : 'B';

    return {
      ideaIdKey:getIdeaIdFromURL,
      challengeKey,
      scoreCardJson: convertJsonParseData || [],
      scoreCardAnswerJson: currentFormData || [],
      scoreCardRating: roundCalcRating,
      userEmail: loginUserEmail,
      ideaEvaluationId: scorecardPopupIdeaEvaluationId,
      businessSCFlag: scorecardPopupFlagRecevive || defaultFlag,
    };
  }, [questions, weightWithIdArr, isHackathonChallenge, getIdeaIdFromURL, challengeKey, convertJsonParseData, loginUserEmail, scorecardPopupIdeaEvaluationId, scorecardPopupFlagRecevive]);

  // Handles Final Submission Process
  // const handleFormChange = useCallback(
  //   async (data) => {
  //     setFormData(data);
  //     const reqWithParams = createPayload(data);

  //     try {
  //       await postInnovateIdeaEvaluatorRatingSaveFn(reqWithParams);
  //       // Note: Modal setDialogEvaluatorRateOpen wrapper removed for full-page operation context.
  //       console.log("Evaluation Form submitted successfully.");
  //     } catch (err) {
  //       console.error('Saved form data Err:', err);
  //     }
  //   }, [createPayload]);

  
  // FIXED: Dynamic Edit action directly using the modified event hook data bundle
  // const handleEditAction = useCallback(async (e) => {
  //   if (e && e.preventDefault) e.preventDefault();
  //   console.log("Saving dynamic form progress with current state:", formData);

  //   const reqWithParams = createPayload(formData); // Uses the updated state synced via onChange
  //   try {
  //     if (handleEvaluatorRatingEdit) {
  //       await handleEvaluatorRatingEdit(reqWithParams);
  //     }
  //     console.log("Progress saved successfully.");
  //   } catch (err) {
  //     console.error("An error occurred during the edit action:", err);
  //     alert("Failed to process edit action. Please try again.");
  //   }
  // }, [formData, createPayload, handleEvaluatorRatingEdit]);



  // Handles BOTH Save and Submit, running validation beforehand
const handleFormChange = useCallback(
  async (data) => {
    setFormData(data);
    const reqWithParams = createPayload(data);

    try {
      if (submitActionType === 'SAVE') {
        // Triggered when SAVE validation succeeds
        await handleEvaluatorRatingEdit(reqWithParams);
        console.log("Evaluation Form saved successfully.");
      } else {
        // Triggered when SUBMIT validation succeeds
        await postInnovateIdeaEvaluatorRatingSaveFn(reqWithParams);
        console.log("Evaluation Form submitted successfully.");
      }
    } catch (err) {
      console.error(`${submitActionType} form data Err:`, err);
    }
  },
  [submitActionType, createPayload, handleEvaluatorRatingEdit, postInnovateIdeaEvaluatorRatingSaveFn]
);


  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  if (getJsonEvaluatorForRatingIsLoading) {
    return <div>Loading...</div>;
  }

  console.log("questions===>", questions, ratingStatusIsPending)

  return (
    <div 
      className="evaluation-page-container formlevel" 
      style={{ 
        maxWidth: '980px',
        boxShadow: '2px 3px 10px #ccc',
        background: '#f7f7f7',
        padding: '2rem',
        margin: '2rem auto' 
      }}
    >
      <h1>{idea?.title}</h1>

      <h4>Please rate the scorecard and complete the form below.</h4>
      <ReactFormGenerator
        data={questions}
        answer_data={prefill || []}
        onSubmit={handleFormChange} 
        onChange={setFormData} // Keeps state updated dynamically on changes before explicit submissions
        hide_actions={!ratingStatusIsPending}
        submitButton={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '20px' }}>
            {/* SUBMIT BUTTON */}
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              borderRadius="reactangle"
              disabled={getJsonEvaluatorForRatingIsLoading}
              onClick={() => setSubmitActionType('SUBMIT')} // Set action flag to SUBMIT
            >
              {getJsonEvaluatorForRatingIsLoading ? "Loading... " : "Submit"}
            </CustomButton>

            {/* EDIT/SAVE BUTTON */}
            {ratingStatusIsPending && (
              <CustomButton
                type="submit" // Changed to "submit" to enforce validation
                variant="outlined"
                color="secondary"
                borderRadius="reactangle"
                onClick={() => setSubmitActionType('SAVE')} // Set action flag to SAVE
              >
                Save
              </CustomButton>
            )}
          </div>
        }

      />
    </div>
  );
};

export default EvaluationScorecardForm;